// Merkan · PDF-Erzeugung im Browser (vektoriell, Text bleibt durchsuchbar). pdfmake wird erst bei Bedarf geladen.
/* eslint-disable @typescript-eslint/no-explicit-any */
import { base64Blob, herunterladen, teilenOderLaden, type TeilDatei } from './teilen';
import { dokumentZeigen } from './ansicht';

export type PdfDok = Record<string, any>;

let geladen: Promise<any> | null = null;
async function pdfmake(): Promise<any> {
  geladen ??= (async () => {
    const [m, f] = await Promise.all([import('pdfmake/build/pdfmake'), import('pdfmake/build/vfs_fonts')]);
    const pm = (m as any).default ?? m;
    const vfs = (f as any).default ?? f;
    if (typeof pm.addVirtualFileSystem === 'function') pm.addVirtualFileSystem(vfs);
    else pm.vfs = vfs;
    return pm;
  })();
  return geladen;
}

/** PDF als Base64 (ohne Data-URL-Präfix) */
export async function pdfBase64(dok: PdfDok): Promise<string> {
  const pm = await pdfmake();
  return pm.createPdf(dok).getBase64();
}

/** pdfmake vorab laden, damit das Teilen-Menü auf dem Smartphone ohne Wartezeit nach dem Tipp öffnet */
export function pdfVorladen(): void {
  void pdfmake().catch(() => { /* wird beim Erzeugen erneut versucht */ });
}

/** PDF als Datei zum Teilen oder Herunterladen */
export async function pdfDatei(dok: PdfDok, dateiname: string): Promise<TeilDatei> {
  return { dateiname, typ: 'application/pdf', daten: base64Blob(await pdfBase64(dok), 'application/pdf') };
}

/** PDF teilen (Smartphone: Teilen-Menü) oder herunterladen (PC). Verlangt der Browser einen neuen Tipp, wird heruntergeladen. */
export async function pdfHerunterladen(dok: PdfDok, dateiname: string): Promise<void> {
  const d = await pdfDatei(dok, dateiname);
  if ((await teilenOderLaden(d)) === 'freigabe') herunterladen(d);
}

/** PDF direkt in der App ansehen (Teilen und Herunterladen in der Ansicht) */
export function pdfZeigen(dok: PdfDok | (() => PdfDok), dateiname: string, titel?: string): void {
  dokumentZeigen({ titel: titel ?? dateiname.replace(/\.pdf$/i, '').replace(/_/g, ' '), dateiname, typ: 'application/pdf', laden: () => pdfDatei(typeof dok === 'function' ? dok() : dok, dateiname) });
}

/** Dateiname ohne problematische Zeichen */
export const dateiname = (s: string) => s.replace(/[^\p{L}\p{N}._ -]+/gu, '-').replace(/\s+/g, '_').replace(/-+/g, '-').slice(0, 120);

export const PDF_FARBE = { text: '#1f2328', leise: '#6b6f76', linie: '#d4d6da', zart: '#eceef1', akzent: '#2f6b5a' };

/** Grundgerüst A4 mit Fußzeile (vier Spalten) und Seitenzahl */
export function pdfGrundlage(o: { inhalt: any[]; fuss: string[][]; seite: string; von: string; titel: string; autor?: string | null }): PdfDok {
  return {
    pageSize: 'A4',
    pageMargins: [56, 48, 48, 96],
    info: { title: o.titel, author: o.autor ?? undefined, creator: 'Merkan' },
    defaultStyle: { font: 'Roboto', fontSize: 9.5, color: PDF_FARBE.text, lineHeight: 1.25 },
    styles: {
      klein: { fontSize: 8, color: PDF_FARBE.leise },
      titel: { fontSize: 15, bold: true, margin: [0, 18, 0, 8] },
      kopf: { bold: true, fontSize: 8.5, color: PDF_FARBE.leise },
    },
    content: o.inhalt,
    footer: (aktuell: number, gesamt: number) => ({
      margin: [56, 18, 48, 0],
      stack: [
        { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 491, y2: 0, lineWidth: 0.5, lineColor: PDF_FARBE.linie }] },
        {
          columns: o.fuss.map((spalte) => ({ width: '*', stack: spalte.filter(Boolean).map((z, i) => ({ text: z, bold: i === 0, fontSize: 7, color: PDF_FARBE.leise })) })),
          columnGap: 10, margin: [0, 6, 0, 0],
        },
        { text: `${o.seite} ${aktuell} ${o.von} ${gesamt}`, alignment: 'right', fontSize: 7, color: PDF_FARBE.leise, margin: [0, 4, 0, 0] },
      ],
    }),
  };
}

/** Tabelle mit dünnen Linien */
export const TABELLEN_LAYOUT = {
  hLineWidth: (i: number, node: any) => (i === 0 ? 0 : i === 1 || i === node.table.body.length ? 0.6 : 0.3),
  vLineWidth: () => 0,
  hLineColor: (i: number) => (i === 1 ? PDF_FARBE.linie : PDF_FARBE.zart),
  paddingLeft: (i: number) => (i === 0 ? 0 : 5),
  paddingRight: (i: number, node: any) => (i === node.table.widths.length - 1 ? 0 : 5),
  paddingTop: () => 4,
  paddingBottom: () => 4,
};
